
import io from "@dw/socket-io-client-esm";
import { startSession } from "./utils.js";

/**
 * Socket.io wrapper 
 */
class SocketClient {

  /**
   * Constructor
   * 
   * @param {string} serverUrl Server URL for the socket.io connection
   * @param {string} preSharedToken Pre-shared token for authentication
   * @param {boolean} autoLogin Automatically login using the pre-shared token
   */
  constructor(serverUrl, preSharedToken, autoLogin = false) {
    this.serverUrl = serverUrl;
    this.preSharedToken = preSharedToken;
    this.autoLogin = autoLogin;
    this.socket = null;
    this.authToken = null;
  }

  // Get auth token using pre-shared token
  async login() {

    // If autoLogin is enabled, use the pre-shared token as the auth token
    if (this.autoLogin) {
      this.authToken = this.preSharedToken
      return;
    }

    // If autoLogin is disabled, get the auth token from the server
    const sharedToken = this.preSharedToken
    const sessionId = generateUUID()

    try {
      // Prepare the request payload
      const requestBody = {
        session: sessionId,
        token: sharedToken,
      };
      // console.log('login', requestBody);

      // Send the request using the reusable function
      const data = await startSession(`${this.serverUrl}/token`, requestBody);

      // Assume the session token is returned in the response
      this.authToken = data.token;
      // console.log('SocketClient : authToken:', this.authToken);
    } catch (error) {
      console.error(`SocketClient : login`, error)
      throw new Error("Login failed with shared token");
    }
  }

  // Load Socket.IO client library from CDN
  async loadSocketIO() {
    if (!window.io) {
      const script = document.createElement("script");
      const socketIoUrl = "https://cdn.socket.io/4.6.1/socket.io.min.js";
      script.src = socketIoUrl;
      document.head.appendChild(script);

      await new Promise((resolve, reject) => {
        script.onload = resolve;
        script.onerror = reject;
      });
    }
  }

  // Connect to the server
  async connect() {
    // Load the Socket.IO client library if not already loaded
    await this.loadSocketIO();

    //update options for io communication
    const options = { auth: { token: this.authToken } }
    this.socket = io(this.serverUrl, options);

    // Basic connection event listeners
    this.socket.on("connect", () => {
      console.log(`Connected to server with id:${this.socket.id}`);
    });

    this.socket.on("disconnect", (reason) => {
      console.log(`Disconnected from server due to reason:${reason}, active:${this.socket.active}`);
      if (!this.socket.active) {
        console.log("Trying reconnection after 3 seconds...");
        setTimeout(() => {
          this.socket.connect();
        }, 3000);
      }
    });

    this.socket.on("connect_error", (error) => {
      console.error("Connection error:", error);
    });
  }

  // Disconnect from the server
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
    }
  }

  // Emit an event to the server
  emit(event, data) {
    if (this.socket) {
      this.socket.emit(event, data);
    }
  }

  // Listen for a specific event from the server
  on(event, callback) {
    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  // Stop listening for a specific event
  off(event, callback) {
    if (this.socket) {
      this.socket.off(event, callback);
    }
  }
}

export default SocketClient;

/**
 * Generates a random UUID (version 4).
 * 
 * @returns {string} The generated UUID.
 */
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}
